import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from './firebase/config';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { el, roleLabel } from './i18n/el';

export default function Layout() {
  const [companyName, setCompanyName] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const profileMenuRef = useRef(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const companyDoc = await getDoc(doc(db, 'companies', user.uid));
        if (companyDoc.exists()) {
          setCompanyName(companyDoc.data().companyName || '');
          setLogoUrl(companyDoc.data().logoUrl || null);
          setUserRole('owner');
          setCompanyId(user.uid);
        } else {
          const membershipDoc = await getDoc(doc(db, 'userMemberships', user.uid));
          if (membershipDoc.exists()) {
            const data = membershipDoc.data();
            setCompanyId(data.companyId);
            // Prefer the authoritative role from the company members doc
            try {
              const memberSnap = await getDoc(doc(db, 'companies', data.companyId, 'members', user.uid));
              const memberRole = memberSnap.exists() ? (memberSnap.data().role || null) : null;
              setUserRole(memberRole || data.role || 'accountant');
            } catch (e) {
              setUserRole(data.role || 'accountant');
            }

            const compRef = doc(db, 'companies', data.companyId);
            const compSnap = await getDoc(compRef);
            if (compSnap.exists()) {
              setCompanyName(compSnap.data().companyName || '');
              setLogoUrl(compSnap.data().logoUrl || null);
            }
          } else {
            navigate('/login');
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, [navigate]);

  // Close sidebar when navigating on mobile
  useEffect(() => {
    setIsSidebarOpen(false);
    setProfileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when sidebar open
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isSidebarOpen]);

  // Close profile menu on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    }
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const getMenuItems = () => {
    const baseItems = [
      { 
        label: el.navInvoices, 
        action: () => navigate('/'),
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
        isActive: location.pathname === '/'
      }
    ];
    if (userRole === 'owner' || userRole === 'manager') {
      baseItems.push({
        label: el.navVatMappings,
        action: () => navigate('/vat-mappings'),
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        ),
        isActive: location.pathname === '/vat-mappings',
      });
    }
    if (userRole === 'owner') {
      baseItems.push({ 
        label: el.navUsers, 
        action: () => navigate('/users'),
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ),
        isActive: location.pathname === '/users'
      });
      baseItems.push({ 
        label: el.navCompany, 
        action: () => navigate('/settings'),
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        ),
        isActive: location.pathname === '/settings'
      });
    }
    // Insert Profile
    baseItems.push({
      label: el.navProfile,
      action: () => navigate('/profile'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A8 8 0 1118.88 6.196M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
      ),
      isActive: location.pathname === '/profile'
    });
    // Logout always present
    baseItems.push({ 
      label: el.navLogout, 
      action: () => handleLogout(),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      ),
      isActive: false
    });
    return baseItems;
  };

  const handleLogout = async () => {
    try { await signOut(auth); navigate('/login'); } catch (e) { console.error(e); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex items-center space-x-2">
          <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
          <span className="text-gray-700">{el.appLoading}</span>
        </div>
      </div>
    );
  }

  const menuItems = getMenuItems();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 lg:hidden sticky top-0 z-20 shadow-md pt-[env(safe-area-inset-top)]">
        <div className="flex items-center justify-between h-14 px-3 sm:px-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="text-white focus:outline-none focus:ring-2 focus:ring-white/50 h-11 w-11 -ml-1 rounded-lg flex items-center justify-center hover:bg-white/10 active:bg-white/20"
              aria-label={el.navOpenMenu}
              aria-expanded={isSidebarOpen}
              aria-controls="mobile-sidebar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16"/></svg>
            </button>
            <img src={process.env.PUBLIC_URL + '/logo/logo.png'} alt="InvoiceBox" className="h-7 w-auto" />
          </div>
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <img src={logoUrl} alt="Company" className="h-8 w-8 rounded-full object-cover ring-1 ring-white/40" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-white/25 text-white flex items-center justify-center text-xs font-semibold ring-1 ring-white/30">
                {companyName ? companyName.charAt(0).toUpperCase() : 'C'}
              </div>
            )}
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setProfileMenuOpen(v => !v)}
                className="h-9 w-9 rounded-full bg-white text-blue-700 flex items-center justify-center text-sm font-semibold ring-1 ring-white/40 focus:outline-none focus:ring-2 focus:ring-white/60"
                aria-haspopup="menu"
                aria-expanded={profileMenuOpen}
              >
                {(auth.currentUser?.displayName || auth.currentUser?.email || 'U').charAt(0).toUpperCase()}
              </button>
              {profileMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl ring-1 ring-black/5 py-1 z-30 origin-top-right">
                  <div className="px-3 py-2.5 border-b">
                    <div className="text-sm font-medium text-gray-800 truncate" title={auth.currentUser?.email || ''}>{auth.currentUser?.displayName || auth.currentUser?.email}</div>
                    {userRole && (<div className="mt-1 text-[11px] inline-block bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{roleLabel(userRole)}</div>)}
                  </div>
                  <button className="w-full text-left px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" onClick={() => { setProfileMenuOpen(false); navigate('/profile'); }}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                    {el.navEditProfile}
                  </button>
                  <button className="w-full text-left px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2" onClick={() => { setProfileMenuOpen(false); handleLogout(); }}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
                    {el.navLogout}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <aside
          id="mobile-sidebar"
          className={`
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            lg:translate-x-0 fixed lg:relative inset-y-0 left-0 z-30 w-[82%] max-w-xs lg:w-64 bg-white shadow-2xl lg:shadow-none lg:border-r lg:border-gray-200
            transform transition-transform duration-300 ease-out lg:transform-none
            flex flex-col justify-between overflow-y-auto
          `}
          aria-hidden={!isSidebarOpen}
        >
          <div className="p-4 border-b lg:hidden flex items-center justify-between">
            <span className="font-semibold text-gray-800">{el.navMenu}</span>
            <button onClick={() => setIsSidebarOpen(false)} className="text-gray-500 h-11 w-11 -mr-2 rounded-lg flex items-center justify-center hover:bg-gray-100" aria-label={el.navCloseMenu}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>

          <div className="p-4 sm:p-5">
            <div className="mb-6 flex items-center justify-center">
              <img src={process.env.PUBLIC_URL + '/logo/logo.png'} alt="InvoiceBox" className="h-11 sm:h-12 w-auto" />
            </div>
            <nav className="space-y-1">
              {menuItems.map((item, idx) => (
                <button
                  key={idx}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[15px] font-medium transition-colors ${item.isActive ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-100' : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100'}`}
                  onClick={() => { item.action(); setIsSidebarOpen(false); }}
                >
                  <span className={`flex-shrink-0 ${item.isActive ? 'text-blue-600' : 'text-gray-400'}`}>{item.icon}</span>
                  <span className="truncate">{item.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              <span>{el.navNeedHelp}</span>
            </div>
          </div>
        </aside>

        <main className="flex-1 overflow-x-hidden overflow-y-auto pb-[env(safe-area-inset-bottom)]">
          {isSidebarOpen && (
            <div className="fixed inset-0 z-20 bg-black/50 backdrop-blur-sm lg:hidden" onClick={() => setIsSidebarOpen(false)}></div>
          )}
          {/* Desktop Top Bar */}
          <div className="hidden lg:flex items-center justify-end bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 h-16 px-6 sticky top-0 z-20 shadow-sm">
            {/* Right: Company summary + Profile */}
            <div className="flex items-center gap-6">
              {/* Company summary */}
              <div className="flex items-center gap-3 pr-4 border-r border-white/20">
                {logoUrl ? (
                  <img src={logoUrl} alt="Company" className="h-11 w-11 rounded-full object-cover ring-2 ring-white/40" />
                ) : (
                  <div className="h-11 w-11 rounded-full bg-white/25 text-white flex items-center justify-center text-sm font-semibold ring-1 ring-white/30">
                    {companyName ? companyName.charAt(0).toUpperCase() : 'C'}
                  </div>
                )}
                <span className="hidden md:inline text-white/95 text-sm xl:text-base font-semibold truncate max-w-[22rem]" title={companyName}>{companyName}</span>
              </div>

              {/* Profile Menu */}
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setProfileMenuOpen((v) => !v)}
                  className="group flex items-center gap-3 rounded-full bg-white/10 hover:bg-white/15 active:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40 px-3 py-1.5 transition"
                  aria-haspopup="menu"
                  aria-expanded={profileMenuOpen}
                >
                  <div className="h-11 w-11 rounded-full bg-white text-blue-700 flex items-center justify-center text-sm font-semibold ring-1 ring-white/40">
                    {(auth.currentUser?.displayName || auth.currentUser?.email || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-white/95 text-sm max-w-[14rem] truncate" title={auth.currentUser?.email || ''}>
                      {auth.currentUser?.displayName || auth.currentUser?.email}
                    </span>
                    {userRole && (
                      <span className="text-white text-[11px] bg-white/20 px-2 py-0.5 rounded-full">{roleLabel(userRole)}</span>
                    )}
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white/80 group-hover:text-white" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z" clipRule="evenodd"/></svg>
                  </div>
                </button>
                {profileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black/5 py-1 z-20">
                    <button
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => { setProfileMenuOpen(false); navigate('/profile'); }}
                    >
                      {el.navEditProfile}
                    </button>
                    <button
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => { setProfileMenuOpen(false); handleLogout(); }}
                    >
                      {el.navLogout}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6">
            <Outlet context={{ userRole, companyId }} />
          </div>
        </main>
      </div>
    </div>
  );
}